import React, { useState, useEffect } from 'react';
import { Button, Modal, Radio, Spin, message } from 'antd';
import { getVideoSetting, updateVideoSetting } from 'api/videoSetting';
import ZoomLogo from 'assets/common/zoom_logo.svg';
import Ico1 from 'assets/videoVisit/ico-1.svg';
import Ico2 from 'assets/videoVisit/ico-2.svg';
import Ico3 from 'assets/videoVisit/ico-3.svg';
import Ico4 from 'assets/videoVisit/ico-4.svg';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import ZoomMeetingLandingPage from 'components/ZoomVideoVisitsBanner/ZoomMeetingLandingPage';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUrlParam } from 'utils/common';
import TreatmentPlanTemplate from './components/TreatmentPlanTemplate';
import ChartingPreferences from './components/ChartingPreferences';

type TSubTab = 'main' | 'templates';

const SUB_TABS: { key: TSubTab; label: string }[] = [
    { key: 'main', label: 'Video & Charting' },
    { key: 'templates', label: 'Visit Notes template' },
];

const getSubTabFromUrl = (): TSubTab => (getUrlParam('sub') === 'templates' ? 'templates' : 'main');

const VideoVisitPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [subTab, setSubTab] = useState<TSubTab>(getSubTabFromUrl);
    // Not saved: the benefits open collapsed every time
    const [showBenefits, setShowBenefits] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showLearnMore, setShowLearnMore] = useState(false);
    const [meetingLinkGenerationType, setMeetingLinkGenerationType] = useState<'KLARITY_MEETING' | 'CUSTOM'>(
        'KLARITY_MEETING',
    );

    // Fetch video settings
    useEffect(() => {
        const fetchVideoSetting = async () => {
            setLoading(true);
            try {
                const result = await getVideoSetting();
                if (result?.data?.data?.meetingLinkGenerationType) {
                    setMeetingLinkGenerationType(result.data.data.meetingLinkGenerationType);
                }
            } catch (err) {
                console.error('Failed to fetch video settings:', err);
                message.error('Failed to fetch video settings. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        fetchVideoSetting();
    }, []);

    React.useEffect(() => {
        setSubTab(getSubTabFromUrl());
    }, [location.search]);

    const handleSubTabChange = (key: TSubTab) => {
        setSubTab(key);
        const url = new URL(window.location.href);
        if (key === 'templates') {
            url.searchParams.set('sub', key);
        } else {
            url.searchParams.delete('sub');
        }
        navigate(`${url.pathname}${url.search}`, { replace: true });
    };

    // Update video settings
    const handleRadioChange = async (e: any) => {
        const newValue = e.target.value as 'KLARITY_MEETING' | 'CUSTOM';
        setSaving(true);
        try {
            const result = await updateVideoSetting(newValue);
            if (result?.data?.code === 200) {
                setMeetingLinkGenerationType(newValue);
                message.success('Saved successfully.');
            } else {
                message.error(result?.data?.message || 'Failed to save settings. Please try again later.');
            }
        } catch (err) {
            console.error('Failed to update video settings:', err);
            message.error('Failed to save settings. Please try again later.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Spin spinning={loading}>
            <div className={s.subTabs}>
                {SUB_TABS.map(({ key, label }) => (
                    <button
                        key={key}
                        type="button"
                        className={`${s.subTab} ${subTab === key ? s.subTabOn : ''}`}
                        onClick={() => {
                            if (subTab !== key) handleSubTabChange(key);
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>
            {subTab === 'templates' && <TreatmentPlanTemplate />}
            {subTab === 'main' && (
                <>
                    <div className={s.wrap}>
                        <div className={s.header}>
                            <h2 className={s.title}>Video visit</h2>
                            <Button
                                className={s.learnMoreBtn}
                                onClick={() => {
                                    setShowLearnMore(true);
                                }}
                            >
                                Learn more
                            </Button>
                        </div>

                        <div className={s.content}>
                            <div className={s.section}>
                                <p className={s.sectionTitle}>Set your default video visit platform</p>
                                <div className={s.optionsContainer}>
                                    <div
                                        className={`${s.optionCard} ${meetingLinkGenerationType === 'KLARITY_MEETING' ? s.selected : ''}`}
                                        onClick={() => {
                                            if (meetingLinkGenerationType !== 'KLARITY_MEETING') {
                                                handleRadioChange({ target: { value: 'KLARITY_MEETING' } });
                                            }
                                        }}
                                    >
                                        <Radio
                                            checked={meetingLinkGenerationType === 'KLARITY_MEETING'}
                                            value="KLARITY_MEETING"
                                            onChange={handleRadioChange}
                                            className={s.radio}
                                        />
                                        <div className={s.optionContent}>
                                            <div className={s.optionHeader}>
                                                <span className={s.optionTitle}>Klarity Meet</span>
                                                <span className={s.recommendedBadge}>Recommended</span>
                                            </div>
                                            <p className={s.optionDescription}>
                                                Automatically generate a secure video link powered by{' '}
                                                <img src={ZoomLogo} alt="Zoom" className={s.zoomLogo} />
                                            </p>
                                        </div>
                                    </div>

                                    <div
                                        className={`${s.optionCard} ${meetingLinkGenerationType === 'CUSTOM' ? s.selected : ''}`}
                                        onClick={() => {
                                            if (meetingLinkGenerationType !== 'CUSTOM') {
                                                handleRadioChange({ target: { value: 'CUSTOM' } });
                                            }
                                        }}
                                    >
                                        <Radio
                                            checked={meetingLinkGenerationType === 'CUSTOM'}
                                            value="CUSTOM"
                                            onChange={handleRadioChange}
                                            className={s.radio}
                                        />
                                        <div className={s.optionContent}>
                                            <p className={s.optionTitle}>Custom link</p>
                                            <p className={s.optionDescription}>
                                                Manually add a video link from your own meeting service.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={s.section}>
                                <p
                                    className={`${s.sectionTitle} ${s.benefitsToggle}`}
                                    onClick={() => setShowBenefits(!showBenefits)}
                                >
                                    Explore the benefits of Klarity Meet integration
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        style={{ transform: `rotate(${showBenefits ? 180 : 0}deg)` }}
                                    >
                                        <path
                                            d="M5.29288 7.29289C5.68341 6.90237 6.31657 6.90237 6.7071 7.29289L9.99998 10.5858L13.2929 7.29289C13.6834 6.90237 14.3166 6.90237 14.7071 7.29289C15.0976 7.68342 15.0976 8.31658 14.7071 8.70711L10.7071 12.7071C10.3166 13.0976 9.6834 13.0976 9.29288 12.7071L5.29288 8.70711C4.90236 8.31658 4.90236 7.68342 5.29288 7.29289Z"
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            fill="#111827"
                                        />
                                    </svg>
                                </p>
                                {showBenefits && (
                                    <div className={s.benefitsContainer}>
                                        <div className={s.benefitsColumn}>
                                            <div className={s.benefitItem}>
                                                <div className={s.benefitIcon}>
                                                    <img src={Ico1} alt="Cost savings" />
                                                </div>
                                                <div className={s.benefitText}>
                                                    <p className={s.benefitTitle}>Cost savings</p>
                                                    <p className={s.benefitDescription}>
                                                        No additional fees for meetings.
                                                    </p>
                                                </div>
                                            </div>

                                            <div className={s.benefitItem}>
                                                <div className={s.benefitIcon}>
                                                    <img src={Ico2} alt="Patients get link automatically" />
                                                </div>
                                                <div className={s.benefitText}>
                                                    <p className={s.benefitTitle}>Patients get link automatically</p>
                                                    <p className={s.benefitDescription}>
                                                        Video links are sent to patients by email and SMS automatically.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={s.benefitsColumn}>
                                            <div className={s.benefitItem}>
                                                <div className={s.benefitIcon}>
                                                    <img src={Ico3} alt="Secure & HIPAA-compliant" />
                                                </div>
                                                <div className={s.benefitText}>
                                                    <p className={s.benefitTitle}>Secure & HIPAA-compliant</p>
                                                    <p className={s.benefitDescription}>
                                                        Secure video visits that meet healthcare privacy standards.
                                                    </p>
                                                </div>
                                            </div>

                                            <div className={s.benefitItem}>
                                                <div className={s.benefitIcon}>
                                                    <img src={Ico4} alt="AI-powered scribing" />
                                                </div>
                                                <div className={s.benefitText}>
                                                    <div className={s.benefitTitleRow}>
                                                        <p className={s.benefitTitle}>AI-powered scribing</p>
                                                        <span className={s.comingSoonBadge}>Coming soon</span>
                                                    </div>
                                                    <p className={s.benefitDescription}>
                                                        Future AI note capture to help you stay focused on your patient.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <ChartingPreferences />
                </>
            )}

            <Modal
                open={showLearnMore}
                onCancel={() => {
                    setShowLearnMore(false);
                }}
                footer={null}
                width="1166px"
                className={commonS.modalFixHeightWithoutPaddingWrap}
                title={
                    <div>
                        Learn more about secure <img className={s.modalTitleIcon} src={ZoomLogo} /> video visits
                    </div>
                }
            >
                <ZoomMeetingLandingPage
                    onGetStarted={() => {}}
                    onClose={() => {
                        setShowLearnMore(false);
                    }}
                />
            </Modal>
        </Spin>
    );
};

export default VideoVisitPage;
